// /stores/project.js
import { defineStore } from "pinia";
import projectService from "@/api/projectService";

const generateRandomProject = (id) => {
  return {
    id,
    name: `Project ${id}`,
    hasNewActivity: id < 4,
    milestone: `Milestone ${Math.ceil(Math.random() * 5)}`,
    targetDate: new Date(
      Date.now() + Math.random() * 10000000000
    ).toLocaleDateString(),
  };
};

export const useProjectStore = defineStore("project", {
  state: () => ({
    projects: [],
    currentProject: null,
    interimProject: null,
    total: 0,
    page: 1,
    size: 5,
    stakeholdersToAdd: [],
    projectWithPendingStakeholdersToAdd: null,
  }),
  actions: {
    // Fetch all projects with pagination
    async fetchAllProjects(page = 1, size = 10) {
      try {
        const params = {
          skip: (page - 1) * size,
          limit: size,
        };
        const { data } = await projectService.getAllProjects(params);
        this.projects = data;
      } catch (error) {
        console.error("Error fetching projects:", error);
        throw error;
      }
    },

    async fetchInterimProject() {
      try {
        const { data } = await projectService.getInterimProject();
        this.interimProject = data; // Store whatever the API returns
        return data;
      } catch (error) {
        console.error("Error fetching interim project:", error);
        throw error;
      }
    },

    // Mock fetch all projects
    async mockFetchAllProjects() {
      const projects = Array.from({ length: 9 }, (_, index) =>
        generateRandomProject(index + 1)
      );
      this.projects = projects;
    },

    // Search projects
    async searchProjects(query = " ", page = 1, size = 10) {
      try {
        const { data } = await projectService.searchProjects(query, page, size);
        this.projects = data.items;
        this.total = data.total;
      } catch (error) {
        console.error("Error searching projects:", error);
        throw error;
      }
    },
    // Fetch project by ID
    async fetchProjectById(id) {
      try {
        const { data } = await projectService.getProjectById(id);
        this.currentProject = data;
        if (!this.projects.find((p) => p.id == id)) {
          this.projects.push(data);
        }
        return data;
      } catch (error) {
        // Handle error
        console.log(error);
        throw error;
      }
    },

    // Create a new project
    async createProject(projectData) {
      try {
        const { data } = await projectService.createProject(projectData);
        // Optionally, add the new project to the projects list
        this.projects.push(data);
      } catch (error) {
        // Handle error
        console.log(error);
        throw error;
      }
    },
    async createProjectWithFiles(formData) {
      try {
        const { data } = await projectService.createProjectWithFiles(formData);
        // Optionally, add the new project to your store
        this.projects.push(data);
        return data;
      } catch (error) {
        console.error("Error creating project with files:", error);
        throw error;
      }
    },

    // Update a project
    async updateProject(id, projectData) {
      try {
        const { data } = await projectService.updateProject(id, projectData);
        // Update the project in the projects list
        const index = this.projects.findIndex((p) => p.id === id);
        if (index !== -1) {
          this.projects.splice(index, 1, data);
        }
        // Update current project if it matches the updated one
        if (this.currentProject && this.currentProject.id === id) {
          this.currentProject = data;
        }
      } catch (error) {
        // Handle error
        console.log(error);
        throw error;
      }
    },
    // Delete a project
    async deleteProject(id) {
      try {
        await projectService.deleteProject(id);
        // Immediately refresh the projects log
        await this.fetchAllProjects();
        // Clear currentProject if it was deleted
        if (this.currentProject && this.currentProject.id === id) {
          this.currentProject = null;
        }
      } catch (error) {
        console.error("Error deleting project:", error);
        throw error;
      }
    },
    setStakeholdersToAdd(stakeholders, projectId) {
      this.stakeholdersToAdd = stakeholders;
      this.projectWithPendingStakeholdersToAdd = projectId;
    },
  },
});
